import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import Paper from "@mui/material/Paper"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import EditIcon from "@mui/icons-material/Edit"
import { Participant, Video, VoiceGroup } from "../../types"
import VideoPlayer from "../../player/VideoPlayer"
import { SubtitleCue } from "../../player/SubtitleParser"
import { StaticField } from "./VideoDetailsSection"
import { MIN_ADMIN_PLAYER_WIDTH } from "./adminTokens"

type PlayerHandle = { seekTo: (time: number) => void }

interface SpeakerAttributionEditorProps {
  video: Video
  subtitleContentUrl?: string
  participants: Participant[]
  voiceGroups: VoiceGroup[]
  speakerLabels: Record<string, string>
  attributions: Record<string, string> // effective cueIndex → participantId (for coloring)
  subtitleOffset: number
  rotationAdjustment?: number
  onRelabelVoice: (tag: string, participantId: string) => void
  onEditCue: (index: number, text: string, lang: string) => void
  // True once this video is sealed. Voice relabeling and cue text edits both
  // write speaker attributions or subtitle cues, which the backend refuses
  // once sealed, so both render as static text instead of live controls.
  // Playback, the language switch and the jump-to-speaker buttons are reads
  // and stay untouched.
  sealed: boolean
}

const colorDot = (color: string): React.CSSProperties => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: color,
  display: "inline-block",
  marginRight: 8,
  flexShrink: 0,
})

const SpeakerAttributionEditor: FC<SpeakerAttributionEditorProps> = ({
  video,
  subtitleContentUrl,
  participants,
  voiceGroups,
  speakerLabels,
  attributions,
  subtitleOffset,
  rotationAdjustment,
  onRelabelVoice,
  onEditCue,
  sealed,
}) => {
  const playerRef = useRef<PlayerHandle | null>(null)
  const [allCues, setAllCues] = useState<SubtitleCue[]>([])
  const [currentCueIndex, setCurrentCueIndex] = useState<number | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingText, setEditingText] = useState("")
  // Locally-applied cue-text edits, keyed by cue index. Layered on top of the
  // cues the player reports via onCueChange so a saved edit is never clobbered
  // by the player pushing its (pre-refetch) cue array back on a playback tick.
  const [editedTexts, setEditedTexts] = useState<Record<number, string>>({})
  const [editorLang, setEditorLang] = useState("")

  // Deliberately NO auto-scroll during playback: the list is a navigation
  // tool — scrolling it must never fight the user. Only the speaker jump
  // buttons scroll it, explicitly.

  // Switching the edited language discards in-flight local text overlays and
  // any open inline editor; the embedded player reloads that language's cues.
  useEffect(() => {
    setEditedTexts({})
    setEditingIndex(null)
  }, [editorLang])

  const getParticipant = useCallback(
    (pid: string | undefined) => participants.find((p) => p.id === pid),
    [participants],
  )

  const handleCueChange = useCallback(
    (cueIndex: number | null, cues: SubtitleCue[]) => {
      setCurrentCueIndex(cueIndex)
      setAllCues(cues)
    },
    [],
  )

  const seekToCue = useCallback(
    (cueIndex: number) => {
      if (allCues[cueIndex]) playerRef.current?.seekTo(allCues[cueIndex].startTime)
    },
    [allCues],
  )

  // Jump to the next line spoken by this participant (after the line
  // currently playing, wrapping around): seek playback there and scroll the
  // list to it.
  const jumpToNextLineBy = useCallback(
    (participantId: string) => {
      if (allCues.length === 0) return
      const from = currentCueIndex ?? -1
      const spokenBy = (i: number) => attributions[String(i)] === participantId
      let target = -1
      for (let i = from + 1; i < allCues.length; i++) {
        if (spokenBy(i)) { target = i; break }
      }
      if (target === -1) {
        for (let i = 0; i <= from && i < allCues.length; i++) {
          if (spokenBy(i)) { target = i; break }
        }
      }
      if (target === -1) return
      seekToCue(target)
      listRef.current
        ?.querySelector(`[data-cue-index="${target}"]`)
        ?.scrollIntoView({ block: "center" })
    },
    [allCues, currentCueIndex, attributions, seekToCue],
  )

  const startEdit = useCallback((i: number, text: string) => {
    setEditingIndex(i)
    setEditingText(text)
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingIndex(null)
    setEditingText("")
  }, [])

  const commitEdit = useCallback(
    (i: number, original: string) => {
      const next = editingText.trim()
      if (next !== "" && next !== original) {
        onEditCue(i, editingText, editorLang)
        // Reflect the edit immediately and keep it sticky against the player's
        // onCueChange overwrites until (and after) the versioned refetch lands.
        setEditedTexts((prev) => ({ ...prev, [i]: editingText }))
      }
      setEditingIndex(null)
      setEditingText("")
    },
    [editingText, onEditCue],
  )

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Row 1 — the player, and the voice assignments beside it. Voices sit
          here rather than above the transcript because assigning a voice is
          the one job you do while watching. */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
        <Box
          sx={{
            // Fixed, not fluid: the control bar has a hard minimum and this
            // mount clears it by 12px. See MIN_ADMIN_PLAYER_WIDTH for the
            // arithmetic and the invariant it rests on. Below a 700px viewport
            // this wraps and goes narrower, which is already past the point
            // where the admin screen is usable.
            flex: "0 0 auto",
            width: MIN_ADMIN_PLAYER_WIDTH,
            maxWidth: "100%",
          }}
        >
          <Paper variant="outlined" sx={{ p: 1 }}>
          <VideoPlayer
            ref={(node) => { playerRef.current = node }}
            src={video.stream_url!}
            type="application/x-mpegURL"
            participants={participants}
            subtitleUrl={subtitleContentUrl}
            subtitleOffset={subtitleOffset}
            speakerAttributions={attributions}
            onCueChange={handleCueChange}
            onPlayStateChange={() => {}}
            rotationAdjustment={rotationAdjustment}
            subtitleLanguages={video.subtitles?.languages}
            masterLanguage={video.subtitles?.master_language}
            language={editorLang || video.subtitles?.master_language}
            onLanguageChange={(l) => setEditorLang(l && l !== video.subtitles?.master_language ? l : "")}
            compact
          />
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 280 }}>
        <Typography variant="overline" color="text.secondary">
          Voices — relabel a whole group
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {voiceGroups.map((g, i) => sealed ? (
            <StaticField
              key={g.tag}
              label={`Voice ${i + 1} — ${g.line_count} lines`}
              value={(() => {
                const p = getParticipant(speakerLabels[g.tag])
                return p ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span style={colorDot(p.color)} />
                    {p.name}
                  </Box>
                ) : <em>Unassigned</em>
              })()}
            />
          ) : (
            <Box key={g.tag} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: "0 0 52px" }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 600, lineHeight: 1.2 }}>
                  Voice {i + 1}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                  {g.line_count} lines
                </Typography>
              </Box>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={speakerLabels[g.tag] || ""}
                onChange={(e) => onRelabelVoice(g.tag, e.target.value)}
                renderValue={(pid) => {
                  const p = getParticipant(pid as string)
                  if (!p) return <em>Unassigned</em>
                  return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <span style={colorDot(p.color)} />
                      {p.name}
                    </Box>
                  )
                }}
              >
                {participants.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <span style={colorDot(p.color)} />
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>
        </Box>
      </Box>

      {/* Row 2 — the transcript, full width. These lines are long and the old
          460px column wrapped most of them. */}
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary">
          Transcript — click a line to play from it
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {participants.map((p) => (
            <Button
              key={p.id}
              size="small"
              variant="outlined"
              onClick={() => jumpToNextLineBy(p.id)}
              title={`Jump to the next line by ${p.name}`}
              sx={{ borderColor: p.color, color: p.color, minWidth: 0 }}
            >
              <span style={colorDot(p.color)} />
              {p.name}
            </Button>
          ))}
        </Box>
        {video.subtitles && (video.subtitles.languages?.length || 0) > 1 && (
          <Box sx={{ mb: 1 }}>
            <label style={{ fontSize: '0.8rem', marginRight: 8 }}>Language:</label>
            <select value={editorLang} onChange={(e) => setEditorLang(e.target.value)}>
              <option value="">Original ({video.subtitles.master_language})</option>
              {video.subtitles.languages
                .filter((l) => l !== video.subtitles!.master_language)
                .map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
            </select>
          </Box>
        )}
        <Box ref={listRef} sx={{ flex: 1, overflowY: "auto", maxHeight: 420, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          {allCues.map((cue, i) => {
            const isActive = i === currentCueIndex
            const p = getParticipant(attributions[String(i)])
            const cueText = editedTexts[i] ?? cue.text
            return (
              <Box
                key={i}
                data-cue-index={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  fontSize: 13,
                  borderLeft: "3px solid",
                  borderLeftColor: p?.color || "transparent",
                  bgcolor: isActive ? "rgba(144, 202, 249, 0.22)" : "transparent",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {editingIndex === i && !sealed ? (
                  <TextField
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onBlur={() => commitEdit(i, cueText)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i, cueText) }
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit() }
                    }}
                    size="small"
                    fullWidth
                    multiline
                    autoFocus
                    onFocus={(e) => {
                      const len = e.target.value.length
                      e.target.setSelectionRange(len, len)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flex: 1 }}
                  />
                ) : (
                  <>
                    <span onClick={() => seekToCue(i)} style={{ cursor: "pointer", flex: 1 }}>
                      {cueText}
                    </span>
                    {!sealed && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); startEdit(i, cueText) }}
                        aria-label="Edit line"
                        sx={{ flexShrink: 0 }}
                      >
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default SpeakerAttributionEditor
